import React, { FC, MouseEvent, useState } from 'react'

import { useForm } from 'react-hook-form'
import { FieldErrors } from 'react-hook-form/dist/types/errors'
import styled from 'styled-components'

import { Icon, Modal, StandardButton, Txt } from 'src/components/atoms'
import { SquareIconButton } from 'src/components/atoms/inputs/SquareIconButton'
import {
  ProjectModalTitle,
  CreateProjectTooltip,
  UpsertProjectBasicInformationInputArea,
} from 'src/components/molecules'

import { EmailDisplayNameMaxLength } from 'src/constants'
import { BaseYup, yup, yupResolver } from 'src/customYup'
import {
  useGA,
  useGetProfile,
  useGetProjects,
  useGetProjectStatuses,
  useProject,
  useToast,
} from 'src/hooks'
import { useGetEmailDomains } from 'src/hooks/useGetEmailDomain'
import { ProfileEntity, ProjectDetailEntity, UpsertProjectFormEntity } from 'src/types'
import { CompassRoleEntity } from 'src/types/entities/CompassRole'

const StyledForm = styled.form`
  max-height: calc(90vh - var(--s5));
  display: flex;
  flex-direction: column;
`

export const AssignUserSchema = BaseYup.object({
  id: BaseYup.number().required(),
  email: BaseYup.string().email().required(),
  name: BaseYup.string().nullable(),
  compassRoleId: BaseYup.number().required('選択ください'),
  compassRolePriority: BaseYup.number().required(),
})

export const upsertProjectSchema: yup.SchemaOf<UpsertProjectFormEntity> = BaseYup.object({
  name: BaseYup.string().required(),
  code: BaseYup.string()
    .matches(/^[\dA-Za-z]*$/, '半角英数字のみを入力してください')
    .max(30, '最大30文字まで入力できます'),
  email: BaseYup.string()
    .email('emailの形式で入力してください')
    // emailDisplayNameが設定されている場合は必須にする
    .when('emailDisplayName', {
      is: (value: string) => value && value.trim().length > 0,
      // eslint-disable-next-line unicorn/no-thenable
      then: BaseYup.string()
        .email('emailの形式で入力してください')
        .required('送信元名を利用する場合は、メールアドレスの入力が必須です。'),
    }),
  emailDisplayName: BaseYup.string().max(
    EmailDisplayNameMaxLength,
    `最大${EmailDisplayNameMaxLength}文字まで入力できます`
  ),
  statusName: BaseYup.string().required('選択ください'),
  zipcode: BaseYup.string(),
  prefecture: BaseYup.string()
    .test('not-selected', '選択ください', value => value !== '未選択')
    .required(),
  city: BaseYup.string(),
  address: BaseYup.string(),
  structureCount: BaseYup.number()
    .nullable()
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
  roomCount: BaseYup.number()
    .min(0, '0以上にしてください')
    .nullable()
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
  planCount: BaseYup.number()
    .nullable()
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
  averagePriceRange: BaseYup.number()
    .nullable()
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
  projectUsers: BaseYup.array(AssignUserSchema),
  externalEditors: BaseYup.array(AssignUserSchema),
  inputExternalEditor: BaseYup.object(),
  isSendAssignEmail: BaseYup.boolean().required(),
})

type Props = {
  isOpen: boolean
  profile: ProfileEntity
  compassRoles: CompassRoleEntity[]
  project?: ProjectDetailEntity
  afterUpsert?: () => void
  refetchProjectDetail?: () => void
  onClose: () => void
}

export const UpsertProjectBasicInformationModal: FC<Props> = ({
  isOpen,
  profile,
  compassRoles,
  project,
  afterUpsert,
  refetchProjectDetail,
  onClose,
}) => {
  const [isLoading, setIsLoading] = useState(false)

  const { projects } = useGetProjects()
  const { projectStatuses, loading: isProjectStatusesLoading } = useGetProjectStatuses()
  const { emailDomains, loading } = useGetEmailDomains()

  const { updateProject, toUpsertFormEntity } = useProject()

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    reset,
    setError,
    formState: { errors },
  } = useForm<UpsertProjectFormEntity>({
    resolver: yupResolver(upsertProjectSchema),
    defaultValues: project
      ? toUpsertFormEntity({ entity: project })
      : {
          prefecture: '未選択',
          statusName: undefined,
          projectUsers: [
            {
              id: profile.id,
              email: profile.email,
              name: profile.name,
              // Compassのロールは固定で配列の最初（バックエンド側でプロジェクト管理者が設定されている）を設定する
              compassRoleId: compassRoles[0].id,
              compassRolePriority: compassRoles[0].priority,
            },
          ],
          externalEditors: [],
          isSendAssignEmail: false,
        },
  })

  const { createProject } = useProject()
  const { displayStandardTextToast, displayErrorTextToast } = useToast()
  const { refetch: refetchProjects } = useGetProjects()
  const { refetch: refetchProfile } = useGetProfile()
  const { sendEvent } = useGA()

  const onSubmit = async (formEntity: UpsertProjectFormEntity) => {
    setIsLoading(true)

    let hasError = false

    if (
      // 許可ドメインが設定されていて
      emailDomains?.length > 0 &&
      // メールアドレスの設定があり
      formEntity.email
    ) {
      // 設定したメールアドレスに指定のドメインが含まれていない場合true
      const hasValidEmailDomain = emailDomains.some(
        emailDomain =>
          emailDomain.allowedDomain &&
          formEntity.email &&
          formEntity.email.includes(emailDomain.allowedDomain)
      )

      if (!hasValidEmailDomain) {
        setError(`email`, { message: 'メールアドレスのドメイン部分が正しくありません' })
        hasError = true
      }
    }

    const isDuplicateProjectCode =
      formEntity.code && projects.some(p => p.id !== project?.id && p.code === formEntity.code)
    if (isDuplicateProjectCode) {
      setError(`code`, { message: '同じ管理コードのプロジェクトが存在します' })
      hasError = true
    }

    if (!formEntity.statusName) {
      setError('statusName', { message: '選択ください' })
      hasError = true
    }

    if (hasError) {
      setIsLoading(false)
      return
    }

    const statusName = formEntity.statusName as string

    try {
      if (project) {
        await updateProject({ projectId: project.id, formEntity })
        await Promise.all([refetchProjects(), refetchProjectDetail?.(), refetchProfile()])
        displayStandardTextToast('更新しました')
        sendEvent('update_status', {
          project_id: project.id,
          status: statusName,
        })
      } else {
        await createProject({ formEntity })
        await Promise.all([refetchProjects(), refetchProfile()])
        displayStandardTextToast('プロジェクトを作成しました')
        sendEvent('create_project', { status: statusName })
      }

      reset()
      onClose()

      afterUpsert?.()
    } catch {
      reset()

      // プロジェクトが存在する（更新時）の場合と存在しない（作成時）の場合でエラーメッセージを変える
      if (project) {
        displayErrorTextToast('プロジェクトの更新に失敗しました')
      } else {
        displayErrorTextToast('プロジェクトの作成に失敗しました')
      }

      onClose()
    } finally {
      setIsLoading(false)
    }
  }

  const onError = (err: FieldErrors<UpsertProjectFormEntity>) => {
    const hasRequiredError = Object.values(err).some(
      // 必須項目のinput未入力（required）、もしくは必須項目dropdownで未選択（not-selected）のエラーがある場合
      error => error?.type === 'required' || error?.type === 'not-selected'
    )
    if (hasRequiredError) {
      displayErrorTextToast('必須項目を入力ください。')
    }
  }

  const onCancel = (event: MouseEvent) => {
    event.preventDefault()
    onClose()
  }

  return (
    // <form>でモーダルのヘッダーにあたる箇所もラップする必要があるためModalのheaderInnerは使用しない
    <Modal isOpen={isOpen} onClose={onClose} canCloseClickOuter={false} maxWidth="md">
      <div className="tw-w-[35.43rem]">
        <StyledForm
          onSubmit={handleSubmit(onSubmit, onError)}
          className="tw-h-full tw-flex tw-flex-col"
        >
          <div className="tw-border-b tw-border-b-border-secondary tw-py-s0 tw-shrink-0">
            <div className="tw-px-s2 tw-w-full">
              <div className="tw-full-justify">
                {project ? (
                  <Txt sizeType="l" className="tw-font-bold tw-text-standardText-primary">
                    基本情報
                  </Txt>
                ) : (
                  <CreateProjectTooltip>
                    <ProjectModalTitle title="プロジェクト作成" />
                  </CreateProjectTooltip>
                )}

                <SquareIconButton size="s" type="button" onClick={onClose}>
                  <Icon path="close/soft/line" size="--r2" />
                </SquareIconButton>
              </div>
            </div>
          </div>
          {profile && !isProjectStatusesLoading && !loading && (
            <UpsertProjectBasicInformationInputArea
              projectStatuses={projectStatuses}
              currentStatus={project?.status}
              emailDomains={emailDomains}
              register={register}
              watch={watch}
              setValue={setValue}
              formErrors={errors}
              clearErrors={clearErrors}
              className="tw-p-s2 tw-overflow-auto tw-shrink"
            />
          )}

          <div className="tw-border-t tw-border-t-border-secondary tw-py-s0 tw-px-s2 tw-shrink-0">
            <div className="tw-ml-auto tw-w-fit">
              <div className="tw-inline-flex tw-gap-s0">
                <StandardButton
                  type="button"
                  buttonType="tertiary"
                  text="キャンセル"
                  onClick={onCancel}
                  disabled={isLoading}
                />
                <StandardButton
                  type="submit"
                  text={isLoading ? undefined : project ? '保存' : '作成'}
                  appendIconPath={isLoading ? 'check/soft/line' : undefined}
                  isLoading={isLoading}
                />
              </div>
            </div>
          </div>
        </StyledForm>
      </div>
    </Modal>
  )
}
